import { patientApiRequest, ApiAuth } from "../../../lib/api";
import {
  BillingDocumentsResponse,
  BillingDocumentsResponseSchema,
  BillingItemsResponse,
  parseBillingItemsResponse,
  BootstrapResponse,
  BootstrapSchema,
  CollectRequestTarget,
  CollectResponse,
  CollectResponseSchema,
  DocumentDownloadResponse,
  DocumentDownloadResponseSchema,
  PaymentConfig,
  PaymentConfigSchema,
  PaymentMethodsResponse,
  PaymentMethodsResponseSchema,
  SetCardNicknameResponse,
  SetCardNicknameResponseSchema,
  SetupIntentResponse,
  SetupIntentResponseSchema,
} from "./types";

export type ItemsParams = {
  status?: "outstanding" | "paid" | "all";
  type?: string | null; // csv
  from?: number | null;
  to?: number | null;
  limit?: number | null;
};

function buildItemsQuery(params: ItemsParams = {}) {
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  if (params.type) search.set("type", params.type);
  if (params.from) search.set("from", String(params.from));
  if (params.to) search.set("to", String(params.to));
  if (params.limit) search.set("limit", String(params.limit));
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export const billingApi = {
  async listItems(auth: ApiAuth, params: ItemsParams = {}): Promise<BillingItemsResponse> {
    const data = await patientApiRequest(auth, {
      method: "GET",
      url: `/patient-app/billing/items${buildItemsQuery(params)}`,
    });
    return parseBillingItemsResponse(data);
  },

  async paymentConfig(auth: ApiAuth, locationId?: string | null): Promise<PaymentConfig> {
    const qs = locationId ? `?locationId=${encodeURIComponent(locationId)}` : "";
    const data = await patientApiRequest(auth, {
      method: "GET",
      url: `/patient-app/billing/payment-config${qs}`,
    });
    return PaymentConfigSchema.parse(data);
  },

  async listDocuments(auth: ApiAuth, itemId: string): Promise<BillingDocumentsResponse> {
    const data = await patientApiRequest(auth, {
      method: "GET",
      url: `/patient-app/billing/items/${encodeURIComponent(itemId)}/documents`,
    });
    return BillingDocumentsResponseSchema.parse(data);
  },

  async documentDownload(
    auth: ApiAuth,
    documentId: string,
    disposition: "attachment" | "inline" = "attachment"
  ): Promise<DocumentDownloadResponse> {
    const params = new URLSearchParams();
    if (disposition !== "attachment") params.set("disposition", disposition);
    const qs = params.toString() ? `?${params.toString()}` : "";
    const data = await patientApiRequest(auth, {
      method: "GET",
      url: `/patient-app/billing/documents/${encodeURIComponent(documentId)}/download${qs}`,
    });
    return DocumentDownloadResponseSchema.parse(data);
  },

  // Paying, for oneself or for a dependent.
  //
  // The two differ in ONE thing: whose charges these are. The canonical route
  // derives the subject AND the payer from the session, which is right exactly
  // while they are the same person; a guardian pays the dependent's charges
  // with their own card, so the subject has to be named — and it is named in
  // the path, never in the body.
  //
  // Everything else is identical, deliberately: the same targets, the same
  // Stripe Elements flow, the same batch engine on the server. A second payment
  // screen for dependents would be a second place for a rounding rule, a
  // minimum or a 3DS redirect to drift.
  //
  // `idempotencyKey` names the attempt (paymentAttempt.ts): the server answers
  // a key once, so a retry of the same attempt is never charged twice.
  async collect(
    auth: ApiAuth,
    body: {
      targets: CollectRequestTarget[];
      locationId?: string | null;
      paymentMethodId?: string | null;
      returnUrl?: string | null;
    },
    dependentPatientId?: string | null,
    idempotencyKey?: string | null
  ): Promise<CollectResponse> {
    const data = await patientApiRequest(auth, {
      method: "POST",
      url: dependentPatientId
        ? `/patient-app/dependents/${encodeURIComponent(dependentPatientId)}/billing/collect`
        : "/patient-app/billing/collect",
      data: body,
      ...(idempotencyKey ? { headers: { "Idempotency-Key": idempotencyKey } } : {}),
    });
    return CollectResponseSchema.parse(data);
  },

  async syncBatch(auth: ApiAuth, batchId: string): Promise<{ status: string }> {
    const data = await patientApiRequest<{ status: string }>(auth, {
      method: "POST",
      url: `/patient-app/billing/payment-batch/${encodeURIComponent(batchId)}/sync`,
      data: {},
    });
    return { status: data.status };
  },
};

export const sessionApi = {
  async bootstrap(auth: ApiAuth): Promise<BootstrapResponse> {
    const data = await patientApiRequest(auth, {
      method: "GET",
      url: "/patient-app/session/bootstrap",
    });
    return BootstrapSchema.parse(data);
  },
};

export const paymentMethodsApi = {
  async list(auth: ApiAuth): Promise<PaymentMethodsResponse> {
    const data = await patientApiRequest(auth, {
      method: "GET",
      url: "/patient-app/payment-methods",
    });
    return PaymentMethodsResponseSchema.parse(data);
  },

  // `locationId` is optional and defaults to the server-side resolution
  // (`patient.homeLocationId` -> `patient.lid[0]`). Pass it whenever the
  // caller already resolved a location for `paymentConfig`, so the
  // publishable key and the SetupIntent are guaranteed to come from the
  // same Stripe account under location-scoped keys.
  async createSetupIntent(
    auth: ApiAuth,
    locationId?: string | null
  ): Promise<SetupIntentResponse> {
    const data = await patientApiRequest(auth, {
      method: "POST",
      url: "/patient-app/payment-methods/setup-intent",
      data: locationId ? { locationId } : {},
    });
    return SetupIntentResponseSchema.parse(data);
  },

  async setDefault(auth: ApiAuth, paymentMethodId: string): Promise<void> {
    await patientApiRequest(auth, {
      method: "POST",
      url: "/patient-app/payment-methods/set-default",
      data: { paymentMethodId },
    });
  },

  // Tell the clinic a card was saved. Only the SetupIntent travels: the server
  // asks Stripe whether it succeeded for this patient and writes the notice.
  async reportAdded(auth: ApiAuth, setupIntentId: string): Promise<void> {
    await patientApiRequest(auth, {
      method: "POST",
      url: "/patient-app/payment-methods/added",
      data: { setupIntentId },
    });
  },

  // Name a card, or clear its name with "" / null. Unlike the calls above this
  // one returns its response: the server trims, strips control characters and
  // caps the length, so the UI must display what was STORED rather than what
  // was typed.
  async setNickname(
    auth: ApiAuth,
    paymentMethodId: string,
    nickname: string | null
  ): Promise<SetCardNicknameResponse> {
    const data = await patientApiRequest(auth, {
      method: "POST",
      url: "/patient-app/payment-methods/set-nickname",
      data: { paymentMethodId, nickname },
    });
    return SetCardNicknameResponseSchema.parse(data);
  },

  async detach(auth: ApiAuth, paymentMethodId: string): Promise<void> {
    await patientApiRequest(auth, {
      method: "DELETE",
      url: `/patient-app/payment-methods/${encodeURIComponent(paymentMethodId)}`,
    });
  },
};
